import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
    Colours,
    getCityFromLocation,
    IconHouse,
    IconDollar,
    Locales,
    messageToString,
    noticeError,
    PrimaryAnchor,
    useSession,
    Sizes,
    PROVINCE,
} from '@ratehub/base-ui';
import { fetchBestRates, SCENARIO_TYPES, useApplyForRateCallback } from '@ratehub/mtg-common';

import { RatePropType } from '../definitions/BestMortgageRatesPropType';
import Paths from '../definitions/Paths';
import {
    DEFAULT_BEST_PURCHASE_PARAMS,
    DEFAULT_MORTGAGE_LOCATION,
    MORTGAGE_TYPES,
} from '../definitions/MortgageTerms';


function HeroTabContentMortgages({
    initialMortgageRates = null,
}) {
    const intl = useIntl();
    const sessionStore = useSession();
    const applyForRate = useApplyForRateCallback();

    const [ rates, setRates ] = useState(initialMortgageRates);
    const [ isLoading, setIsLoading ] = useState(true);

    // We only want a few rates in a specific order
    const featuredRates = useMemo(
        () => {
            return rates?.length
                ? [
                    rates.find(({ type, term }) => type === MORTGAGE_TYPES.FIXED && term === 60),
                    rates.find(({ type, term }) => type === MORTGAGE_TYPES.VARIABLE && term === 60),
                    rates.find(({ type, term }) => type === MORTGAGE_TYPES.FIXED && term === 36),
                ]
                : [ {}, {}, {} ]; // Skeleton rates for skeleton loaders
        },
        [ rates ],
    );

    const verifiedLocation = useMemo(() => {
        const isSessionLocationAvailable = sessionStore?.interestLocation?.city
            && sessionStore?.interestLocation?.province;

        if (!isSessionLocationAvailable) {
            return DEFAULT_MORTGAGE_LOCATION;
        }

        const isValidCanadianLocation = Object.values(PROVINCE)
            .includes(sessionStore.interestLocation.province.toUpperCase());

        if (isValidCanadianLocation) {
            return sessionStore.interestLocation;
        } else {
            noticeError(
                new Error('Invalid interest location received from session'),
                {
                    message: '[HeroTabContentMortgages] Invalid interest location',
                    extra: {
                        receivedLocation: sessionStore.interestLocation,
                    },
                },
            );
            return DEFAULT_MORTGAGE_LOCATION;
        }
    }, [ sessionStore.interestLocation ]);

    // Refetch new rates for users session location if different from default location
    useEffect(() => {
        const hasSessionBeenChecked = !sessionStore.hasFetchedSession && !sessionStore.fetchingSessionError;

        if (hasSessionBeenChecked) {
            return;
        }

        const isInterestLocationEqualToDefaultLocation = (
            verifiedLocation.city.toLowerCase() === DEFAULT_MORTGAGE_LOCATION.city.toLowerCase()
            && verifiedLocation.province.toLowerCase() === DEFAULT_MORTGAGE_LOCATION.province.toLowerCase()
        );

        if (isInterestLocationEqualToDefaultLocation) {
            // Show default rates
            setIsLoading(false);
            return;
        }

        async function getRates({ city, province } = {}) {
            const MIN_LOADING_TIME = 2_000;
            const startTime = Date.now();

            let response;

            try {
                response = await fetchBestRates(
                    SCENARIO_TYPES.PURCHASE,
                    {
                        ...DEFAULT_BEST_PURCHASE_PARAMS,
                        isFeatured: 1,
                        province,
                        city,
                        type: [ MORTGAGE_TYPES.FIXED, MORTGAGE_TYPES.VARIABLE ],
                        term: [ 60, 36 ],
                    },
                );
                if (!response.length) {
                    throw new Error('Received 0 rates in response');
                }
            } catch (error) {
                response = initialMortgageRates;

                noticeError(
                    error,
                    {
                        message: '[HeroTabContentMortgages] Error fetching best mortgage rates',
                    },
                );
            } finally {
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
                setTimeout(() => {
                    setIsLoading(false);
                    setRates(response);
                }, remainingTime);
            }
        }

        getRates(verifiedLocation);
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
        sessionStore.hasFetchedSession,
        sessionStore.interestLocation?.city,
        sessionStore.interestLocation?.province,
        verifiedLocation,
    ]);

    function handleRateClick(rate) {
        applyForRate({
            rate,
            rateFetchingOptions: {
                homePrice: DEFAULT_BEST_PURCHASE_PARAMS.homePrice,
                downPaymentPercent: DEFAULT_BEST_PURCHASE_PARAMS.downPaymentPercent,
                amortization: DEFAULT_BEST_PURCHASE_PARAMS.amortization,
                term: rate.term,
                type: rate.type,
                city: getCityFromLocation(verifiedLocation),
                scenario: SCENARIO_TYPES.PURCHASE,
            },
        });
    }

    const bestRatesLink = intl.locale === Locales.FRENCH
        ? (Paths.BEST_MORTGAGE_RATES_FR ?? Paths.BEST_MORTGAGE_RATES_EN)
        : Paths.BEST_MORTGAGE_RATES_EN;

    const ctaBuyingLink = intl.locale === Locales.FRENCH
        ? (Paths.BUYING_MORTGAGE_FR ?? Paths.BUYING_MORTGAGE_EN)
        : Paths.BUYING_MORTGAGE_EN;

    const ctaRenewingLink = intl.locale === Locales.FRENCH
        ? (Paths.RENEWING_MORTGAGE_FR ?? Paths.RENEWING_MORTGAGE_EN)
        : Paths.RENEWING_MORTGAGE_EN;

    return (
        <Container className="rh-display-flex rh-flex-wrap rh-justify-content-space-between rh-gap-1">
            {/* Column 1 */}
            <div className="col-1 rh-p-1 rh-border-radius-8px rh-border-color-blueberry-dark rh-border-width-1px rh-border-style-solid rh-display-grid rh-gap-1">
                <div className="rh-display-flex rh-justify-content-space-between rh-align-items-center">
                    <h2 className="rh-m-0 rh-text-l weight-medium">
                        <FormattedMessage
                            id="web-components.HeroTabContentMortgages.col1.title"
                            defaultMessage="Compare best rates"
                        />
                    </h2>
                    <a
                        href={bestRatesLink}
                        className="rh-fg-blueberry-dark rh-text-m"
                        data-name="hero-tab-content-mortgages-view-all-rates-link"
                    >
                        <FormattedMessage
                            id="web-components.HeroTabContentMortgages.col1.seeAllRates"
                            defaultMessage="View all"
                        />
                    </a>
                </div>
                <div className="rh-display-grid rh-align-items-stretch rh-gap-1 rh-position-relative">
                    <For
                        each="rateInfo"
                        of={featuredRates}
                        index="index"
                    >
                        <MortgageRate
                            key={index}
                            isLoading={isLoading}
                            rate={rateInfo}
                            onClick={handleRateClick}
                        />
                    </For>
                </div>
            </div>

            {/* Column 2 */}
            <div className="col-2 rh-bg-stone-lightest rh-p-1 rh-border-radius-8px rh-text-align-center rh-display-flex rh-flex-direction-column rh-justify-content-space-between rh-gap-1">
                <div className="image-wrapper rh-position-relative rh-width-100p rh-height-100p">
                    <img
                        // Placeholder image until we have a proper design
                        src="https://media.istockphoto.com/id/1356074032/photo/excited-family-moving-in-new-home.jpg?s=2048x2048&w=is&k=20&c=rHj4MdV4vfh8V-rowJUX5hrlVKGarM_dkIxDB1c1N0g="
                        alt={messageToString(
                            <FormattedMessage
                                id="web-components.HeroTabContentMortgages.img.alt"
                                defaultMessage="Happy smiling family with one child standing in new home on moving day."
                            />,
                            intl,
                        )}
                        className="rh-position-absolute"
                    />
                </div>
                <div className="rh-display-flex rh-flex-direction-column rh-gap-1">
                    <div className="rh-display-flex rh-flex-direction-column rh-gap-1">
                        <h2 className="rh-fg-blueberry-darkest weight-medium rh-title-l rh-m-0">
                            <FormattedMessage
                                id="web-components.HeroTabContentMortgages.col2.title"
                                defaultMessage="Get your mortgage rate quote in under 2 mins"
                            />
                        </h2>
                        <p className="rh-text-l rh-m-0">
                            <FormattedMessage
                                id="web-components.HeroTabContentMortgages.col2.subtitle"
                                defaultMessage="You can have it all. The best rate and the best customer service from our team of award-winning mortgage experts."
                            />
                        </p>
                    </div>
                    <div className="cta-container rh-display-flex rh-flex-wrap rh-gap-1">
                        <PrimaryAnchor
                            href={ctaBuyingLink}
                            size="medium"
                            className="cta"
                            data-name="hero-tab-content-buying-link"
                        >
                            <IconHouse
                                stroke={Colours.COCONUT}
                                strokeWidth="3px"
                                width="1.5rem"
                                height="1.5rem"
                                className="rh-mr-0_5"
                            />
                            <FormattedMessage
                                id="web-components.HeroTabContentMortgages.col2.button.buying"
                                defaultMessage="I'm buying"
                            />
                        </PrimaryAnchor>
                        <PrimaryAnchor
                            href={ctaRenewingLink}
                            size="medium"
                            className="cta"
                            data-name="hero-tab-content-renewing-link"
                        >
                            <IconDollar
                                stroke={Colours.COCONUT}
                                strokeWidth="3px"
                                width="1.5rem"
                                height="1.5rem"
                                className="rh-mr-0_5"
                            />
                            <FormattedMessage
                                id="web-components.HeroTabContentMortgages.col2.button.renewing"
                                defaultMessage="I'm renewing / refinancing"
                            />
                        </PrimaryAnchor>
                    </div>
                </div>
            </div>
        </Container>
    );
}

HeroTabContentMortgages.propTypes = {
    initialMortgageRates: PropTypes.arrayOf(RatePropType).isRequired,
};

const Container = styled.div`
    > .col-1 {
        flex: 1 1 250px;
        grid-template-rows: auto 1fr;
    }

    > .col-2 {
        flex: 3 1 200px;

        > .image-wrapper {
            min-height: 15rem;

            > img {
                max-width: 100%;
                max-height: 100%;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                object-fit: cover;
                border-radius: 100vh;
                aspect-ratio: 1/1;
            }
        }

        .cta-container {
            > .cta {
                flex: 1 1 200px;
            }
        }
    }
`;

function MortgageRate({
    isLoading,
    rate,
    onClick,
}) {
    const showSkeleton = useMemo(() => {
        return isLoading || !(rate?.value > 0);
    }, [ isLoading, rate?.value ]);

    const rateYears = useMemo(() => {
        return rate?.term
            ? rate.term / 12
            : null;
    }, [ rate?.term ]);

    return (
        <MortgageRateButton
            onClick={() => onClick(rate)}
            className={classNames(
                'rh-display-grid rh-text-decoration-none rh-overflow-hidden rh-p-0',
                'rh-border-color-blueberry-dark rh-border-style-solid rh-border-width-1px rh-border-radius-5px',
            )}
            disabled={showSkeleton}
            data-name={`mortgage-card-cta-${rateYears}-years-${rate?.type}`}
        >
            <div className="rh-display-flex rh-justify-content-space-between rh-align-items-center rh-gap-1 rh-p-1">
                <span
                    className={classNames(
                        'weight-regular rh-text-l rh-width-fit-content rh-text-align-left',
                        { 'rh-fg-blackberry' : !showSkeleton },
                        { 'skeleton rh-bg-stone rh-border-radius-4px rh-fg-transparent': showSkeleton },
                    )}
                    data-name="hero-tab-content-mortgages-rate-type-and-term"
                >
                    <FormattedMessage
                        id="web-components.HeroTabContentMortgages.mortgageRate.typeAndTerm"
                        defaultMessage="{term}-year {type}"
                        values={{
                            term: rateYears,
                            type: rate?.type,
                        }}
                    />
                </span>
                <span
                    className={classNames(
                        'rate-percent rh-text-align-right rh-m-0 weight-medium',
                        { 'rh-fg-blueberry-darkest' : !showSkeleton },
                        { 'skeleton rh-bg-stone rh-border-radius-4px rh-fg-transparent rh-max-width-8': showSkeleton }
                    )}
                    data-name="hero-tab-content-mortgages-rate-value"
                >
                    <FormattedNumber
                        value={rate?.value/100}
                        format="percent2"
                    />
                </span>
            </div>
            <div className="rh-p-0_25 rh-text-m rh-bg-blueberry-lightest rh-fg-blueberry-dark rh-text-align-center weight-medium">
                <FormattedMessage
                    id="web-components.HeroTabContentMortgages.mortgageRate.inquire"
                    defaultMessage="inquire"
                />
            </div>
        </MortgageRateButton>
    );
}

MortgageRate.propTypes = {
    isLoading: PropTypes.bool,
    rate: PropTypes.oneOfType([
        RatePropType,
        PropTypes.object, // skeleton
    ]).isRequired,
    onClick: PropTypes.func,
};

const MortgageRateButton = styled.button`
    grid-template-rows: 1fr auto;

    .rate-percent {
        font-size: min(${Sizes.FONTS['4XL']}, 8vw);
    }

    .skeleton {
        animation: pulse 2s ease-in-out infinite;
        -webkit-user-select: none;
        user-select: none;
    }

    @keyframes pulse {
        0% { opacity: 0.8; }
        50% { opacity: 0.3; }
        100% { opacity: 0.8; }
    }
`;

export default observer(HeroTabContentMortgages);
